import * as THREE from 'three';
import { CHUNK_SIZE, WATER_LEVEL } from './TerrainConstants.js';

/**
 * Utility functions for terrain generation and manipulation
 * Contains pure helper functions for terrain operations
 */

/**
 * Generates a basic terrain mesh with random height variation
 * @param {number} size - Size of the terrain plane
 * @param {number} segments - Number of segments for the plane geometry
 * @returns {THREE.PlaneGeometry} Generated terrain geometry
 */
export function generateTerrain(size = 500, segments = 128) {
    const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
    const heightMap = new Array(segments + 1).fill(0).map(() =>
        new Array(segments + 1).fill(0).map(() =>
            (Math.random() - 0.5) * 3
        )
    );

    const positions = geometry.attributes.position.array;
    for (let i = 0; i < positions.length; i += 3) {
        const x = Math.floor(i / 3) % (segments + 1);
        const y = Math.floor(i / (3 * (segments + 1)));
        positions[i + 2] = heightMap[x][y];
    }

    geometry.computeVertexNormals();
    return geometry;
}

/**
 * Flattens terrain heights to a specified level
 * @param {Array<Array<number>>} heightMap - Height map to flatten
 * @param {number} targetHeight - Target height to flatten to
 * @returns {Array<Array<number>>} Flattened height map
 */
export function flattenTerrainHeights(heightMap, targetHeight) {
    return heightMap.map(row => 
        row.map(height => Math.max(height, targetHeight))
    );
}

/**
 * Creates a height map for a chunk
 * @param {number} size - Size of the height map
 * @param {number} defaultValue - Default height value
 * @returns {Array<Array<number>>} Generated height map
 */
export function createHeightMap(size, defaultValue = 0) {
    return new Array(size + 1).fill(0)
        .map(() => new Array(size + 1).fill(defaultValue));
}

/**
 * Applies a Gaussian smoothing filter to terrain heights
 * @param {Array<Array<number>>} heightMap - 2D array of height values
 * @param {number} radius - Smoothing radius (larger = smoother)
 * @param {boolean} preserveEdges - Whether to preserve edge behavior (default: false)
 * @returns {Array<Array<number>>} Smoothed height map
 */
export function smoothHeightMap(heightMap, radius = 1, preserveEdges = false) {
    // Safety check: if heightMap is undefined, return an empty heightmap
    if (!heightMap || !Array.isArray(heightMap) || heightMap.length === 0) {
        console.warn('Invalid heightMap provided to smoothHeightMap');
        return createHeightMap(CHUNK_SIZE, 0);
    }
    
    const size = heightMap.length;
    // Ensure we create a properly sized result array
    const result = createHeightMap(size - 1, 0);
    
    // Convert non-integer radius to integer ceiling
    const intRadius = Math.ceil(radius);
    
    // Performance optimization: Pre-calculate Gaussian weights for all possible distances
    // This avoids recalculating the expensive Math.exp() for each cell
    const maxDistance = intRadius * 2;
    const weightCache = new Map();
    
    // Apply more aggressive smoothing with higher weight for closer neighbors
    for (let x = 0; x < size; x++) {
        for (let z = 0; z < size; z++) {
            let sum = 0;
            let weightSum = 0;
            
            // Determine if this is an edge cell that needs special treatment
            const isEdgeCell = preserveEdges && (
                x < 2 || x >= size - 2 || 
                z < 2 || z >= size - 2
            );
            
            // For edge cells, use a larger radius and directional weighting
            const smoothingRadius = isEdgeCell ? intRadius * 2 : intRadius * 2;
            
            // Skip every other point for large chunks and high radiuses (performance optimization)
            const skipFactor = radius > 2.0 ? 2 : 1;
            
            // Use integer radius for loop bounds with larger sampling area
            for (let ox = -smoothingRadius; ox <= smoothingRadius; ox += skipFactor) {
                for (let oz = -smoothingRadius; oz <= smoothingRadius; oz += skipFactor) {
                    const nx = x + ox;
                    const nz = z + oz;
                    
                    if (nx >= 0 && nx < size && nz >= 0 && nz < size) {
                        try {
                            // Use improved Gaussian weights with stronger falloff
                            const distance = Math.sqrt(ox*ox + oz*oz);
                            
                            // Skip points outside the radius
                            if (distance > smoothingRadius) continue;
                            
                            // Apply directional weighting for edge cells
                            let directionalWeight = 1.0;
                            if (isEdgeCell) {
                                // Determine if sampling toward center
                                const isTowardCenter = 
                                    (x < 2 && ox > 0) || 
                                    (x >= size - 2 && ox < 0) ||
                                    (z < 2 && oz > 0) || 
                                    (z >= size - 2 && oz < 0);
                                
                                // Stronger weight for samples toward center
                                directionalWeight = isTowardCenter ? 1.5 : 0.8;
                            }
                            
                            // Use stronger weight falloff for more natural terrain transitions
                            // Using cached weight calculation for performance
                            let gaussWeight = weightCache.get(distance);
                            if (gaussWeight === undefined) {
                                gaussWeight = Math.exp(-(distance*distance)/(2.5*radius*radius));
                                weightCache.set(distance, gaussWeight);
                            }
                            
                            // Apply combined weight
                            const weight = gaussWeight * directionalWeight;
                            
                            // Safety check to prevent undefined access
                            if (heightMap[nx] && typeof heightMap[nx][nz] === 'number') {
                                sum += heightMap[nx][nz] * weight;
                                weightSum += weight;
                            }
                        } catch (err) {
                            console.warn(`Error accessing heightMap at [${nx}][${nz}]`);
                            // Continue with other cells
                        }
                    }
                }
            }
            
            // Safety check to ensure valid values
            if (weightSum > 0) {
                result[x][z] = sum / weightSum;
            } else if (heightMap[x] && typeof heightMap[x][z] === 'number') {
                result[x][z] = heightMap[x][z];
            } else {
                // Default value if we can't access the original height
                result[x][z] = 0;
            }
        }
    }
    
    return result;
}

/**
 * Performs bilinear interpolation for smooth height transitions
 * @param {number} h00 - Height at (0,0)
 * @param {number} h10 - Height at (1,0)
 * @param {number} h01 - Height at (0,1)
 * @param {number} h11 - Height at (1,1)
 * @param {number} fx - Fractional X coordinate
 * @param {number} fz - Fractional Z coordinate
 * @returns {number} Interpolated height
 */
export function interpolateHeight(h00, h10, h01, h11, fx, fz) {
    return (h00 * (1 - fx) * (1 - fz)) +
           (h10 * fx * (1 - fz)) +
           (h01 * (1 - fx) * fz) +
           (h11 * fx * fz);
}

/**
 * Converts world coordinates to local chunk coordinates
 * @param {number} worldX - World X coordinate
 * @param {number} worldZ - World Z coordinate
 * @param {number} chunkX - Chunk X index
 * @param {number} chunkZ - Chunk Z index
 * @returns {Object} Local coordinates {localX, localZ}
 */
export function getLocalCoordinates(worldX, worldZ, chunkX, chunkZ) {
    const localX = Math.floor(worldX - chunkX * CHUNK_SIZE);
    const localZ = Math.floor(worldZ - chunkZ * CHUNK_SIZE);
    return { localX, localZ };
}

/**
 * Checks if coordinates are within chunk bounds
 * @param {number} x - X coordinate
 * @param {number} z - Z coordinate
 * @returns {boolean} Whether coordinates are valid
 */
export function isWithinChunkBounds(x, z) {
    return x >= 0 && x < CHUNK_SIZE && z >= 0 && z < CHUNK_SIZE;
} 